import { useState } from "react";
import { Popover } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import { observer } from "mobx-react-lite";
import { Oval } from "react-loader-spinner";
import DataRepoQuestionKeysPanelBody from "./DataRepoQuestionKeysPanelBody";

interface DataRepoQuestionKeysDropdownProps {
  active: boolean;
  keys: string[];
  loading: boolean;
  fetchError: string | null;
}

const DataRepoQuestionKeysDropdown = observer(function DataRepoQuestionKeysDropdown({
  active,
  keys,
  loading,
  fetchError,
}: DataRepoQuestionKeysDropdownProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const onCopyKey = async (k: string) => {
    try {
      await navigator.clipboard.writeText(k);
      setCopiedKey(k);
      window.setTimeout(() => setCopiedKey(null), 1600);
    } catch {
      setCopiedKey(null);
    }
  };

  if (!active) return null;

  const label = (() => {
    if (loading && keys.length === 0 && !fetchError) return null;
    if (fetchError) return "Keys";
    if (keys.length > 0) return `${keys.length} ${keys.length === 1 ? "key" : "keys"}`;
    return "Keys";
  })();

  return (
    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
      <Popover className="relative z-20 inline-block max-w-full">
        {({ open }) => (
          <>
            <Popover.Button
              type="button"
              onClick={(e) => e.stopPropagation()}
              aria-label="View question field keys, click to copy"
              className={`inline-flex max-w-full items-center gap-0.5 rounded-md py-0.5 pl-0 pr-0.5 text-left text-[11px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
                open
                  ? "text-indigo-700"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {label == null ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-slate-400">
                  <Oval height={12} width={12} color="#94a3b8" ariaLabel="Loading" visible />
                  <span>Loading keys</span>
                </span>
              ) : (
                <span className="whitespace-nowrap">{label}</span>
              )}
              <ChevronDown
                size={13}
                strokeWidth={2}
                className={`shrink-0 text-slate-400 transition-transform duration-200 ${
                  open ? "rotate-180" : ""
                }`}
                aria-hidden
              />
            </Popover.Button>

            <Popover.Panel
              portal
              anchor="bottom start"
              transition
              className="z-[300] mt-1 w-[min(calc(100vw-32px),300px)] rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg outline-none transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
            >
              <DataRepoQuestionKeysPanelBody
                keys={keys}
                loading={loading}
                fetchError={fetchError}
                copiedKey={copiedKey}
                onCopyKey={onCopyKey}
              />
            </Popover.Panel>
          </>
        )}
      </Popover>
    </div>
  );
});

export default DataRepoQuestionKeysDropdown;
